import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'

import * as styles from './RegistrationConfirmationFrame.module.scss'

import {
  ModelErrorResponse,
  ModelErrorResponseErrorCodeEnum,
} from '../../apis/fvillage'
import {
  AgreementForm,
  Button,
  CancelPolicySection,
  ErrorCard,
  FormNote,
  Loader,
  MainColumn,
  MultiColumn,
  NotesSection,
  OptionAnswersSection,
  RegistrationConfirmationHead,
  RegistrationStepper,
  ReservationDetailButton,
  ReservationLayout,
  ReservationPanel,
  SideColumn,
  Text,
} from '../../components'
import {
  logCoupon,
  logReservation,
} from '../../components/contents/Braze/brazeClient'
import { PATH, REGISTRATION_PATH } from '../../constants'
import {
  useAuthProvider,
  useCreateNextPath,
  useCreateReservationApi,
  useLogin,
  useOptions,
  useOptionsApi,
  usePriceApi,
  usePublication,
  useRegistrationState,
  useReservationState,
  useReservationsRecentApi,
  useSearchQueryParams,
  useTransactionApi,
} from '../../hooks'
import { Agreement, agreementSchema } from '../../schemas'
import {
  isInvoiceAccessKeyNoLongerValidError,
  isInvoicePayment,
  requiresPayment,
} from '../../utils'

const translateNamespace = 'frames.RegistrationConfirmationFrame'

export const RegistrationConfirmationFrame = () => {
  const { token } = useAuthProvider()
  const {
    params: { transaction: transactionId },
  } = useSearchQueryParams()
  const {
    data: transaction,
    error,
    isLoading: isTransactionLoading,
  } = useTransactionApi(transactionId, token)
  const { t, i18n } = useTranslation()
  const { onCreateNextPath } = useCreateNextPath()
  const { onUpdateReservationState } = useReservationState()
  const { trigger: createReservation, error: apiError } =
    useCreateReservationApi(token, transactionId)
  const { onClearRegistrationStateByKey } = useRegistrationState()
  const publication = usePublication(
    i18n.language,
    transaction?.input?.bookingEngineHotel?.publications
  )
  const [isPageError, setPageError] = useState(false)
  const { onGet: onGetOptions } = useOptions()
  const optionItems = onGetOptions()
  const hasOptionAnswers =
    transaction?.input?.optionAnswers === undefined &&
    optionItems.some(
      (item) => item.count && item.answers?.some(({ value }) => value?.trim())
    )
  // Older backends omit snapshots, so local answers still need current labels (SB-2908).
  const { data: optionsData } = useOptionsApi({
    property: hasOptionAnswers
      ? transaction?.input?.bookingEngineHotel?.slug
      : undefined,
    room: transaction?.input?.bookingEngineRoom?.slug,
    plan: transaction?.input?.bookingEnginePlan?.slug,
    checkInDate: transaction?.input?.checkInDate ?? '',
    checkOutDate: transaction?.input?.checkOutDate ?? '',
    numberOfAdults: transaction?.input?.numberOfAdults ?? 0,
    numberOfChildren: transaction?.input?.numberOfChildren ?? 0,
    numberOfInfants: transaction?.input?.numberOfInfants ?? 0,
    roomCount: transaction?.input?.roomCount ?? 0,
  })

  const navigate = useNavigate()
  const { onValidateTokenExpired } = useLogin()
  const [isPriceLoaded, setPriceLoaded] = useState(false)
  const [isReservationLoading, setReservationLoading] = useState(false)

  const formMethods = useForm<Agreement>({
    defaultValues: {
      agreement: false,
    },
    resolver: zodResolver(agreementSchema),
  })

  const [errorCord, setErrorCode] = useState<
    ModelErrorResponseErrorCodeEnum | 'default' | undefined
  >(undefined)
  const [isInvoiceAccessKeyNoLongerValid, setInvoiceAccessKeyNoLongerValid] =
    useState(false)

  const { data: priceData } = usePriceApi({
    token,
    checkInDate: transaction?.input?.checkInDate ?? '',
    checkOutDate: transaction?.input?.checkOutDate ?? '',
    numberOfAdults: transaction?.input?.numberOfAdults ?? 0,
    numberOfChildren: transaction?.input?.numberOfChildren ?? 0,
    numberOfInfants: transaction?.input?.numberOfInfants ?? 0,
    propertySlug: transaction?.input?.bookingEngineHotel?.slug ?? '',
    roomSlug: transaction?.input?.bookingEngineRoom?.slug ?? '',
    planSlug: transaction?.input?.bookingEnginePlan?.slug ?? '',
    roomCount: transaction?.input?.roomCount ?? 0,
    transactionId,
    reservationId: transaction?.reservationId,
  })

  useEffect(() => {
    // Handle api error response.
    if (apiError || error) {
      if (isAxiosError(apiError)) {
        // If token expired, redirect to login page.
        onValidateTokenExpired(apiError)
      }
      if (isAxiosError(error)) {
        onValidateTokenExpired(error)
      }

      setReservationLoading(false)
      if (
        isAxiosError(apiError) &&
        apiError.response &&
        apiError.response.data
      ) {
        const modelError: ModelErrorResponse = apiError.response.data
        setErrorCode(modelError.errorCode ?? 'default')
        setInvoiceAccessKeyNoLongerValid(
          isInvoiceAccessKeyNoLongerValidError(apiError)
        )
      }
    }
  }, [error, apiError, onValidateTokenExpired])

  const { handleSubmit, watch } = formMethods
  const isAgreed = watch('agreement', false)

  const onSubmit = handleSubmit(async () => {
    if (!isPriceLoaded) {
      return
    }
    setReservationLoading(true)
    try {
      const res = await createReservation()
      if (res && res.data.confirmationCode && res.data.id) {
        onClearRegistrationStateByKey('specialServiceRequest')
        onClearRegistrationStateByKey('companyName')
        onClearRegistrationStateByKey('purposeOfUse')
        onClearRegistrationStateByKey('checkInTime')
        onClearRegistrationStateByKey('invoiceAddressee')
        onUpdateReservationState({
          confirmationCode: res.data.confirmationCode,
          reservationId: res.data.id.toString(),
        })

        const optionDetails =
          priceData?.options && priceData.options.length > 0
            ? priceData.options.map((opt) => ({
                id: opt.id ?? 0,
                quantity: opt.unit ?? 0,
                price: opt.price ?? 0,
                total: opt.total ?? 0,
              }))
            : undefined

        logReservation({
          price: priceData?.totalPrice ?? 0,
          quantity: transaction?.input?.roomCount,
          propertySlug: transaction?.input?.bookingEngineHotel?.slug ?? '',
          planId: transaction?.input?.bookingEnginePlan?.id,
          options: optionDetails,
        })
        if (
          typeof priceData?.discountPrice === 'number' &&
          typeof priceData?.discountId === 'number'
        ) {
          // Send the coupon price as a negative amount
          const discountPrice =
            priceData.discountPrice > 0
              ? -priceData.discountPrice
              : priceData.discountPrice
          logCoupon({
            price: discountPrice,
            couponId: priceData.discountId,
            couponName: priceData?.discountName ?? '',
            propertySlug: transaction?.input?.bookingEngineHotel?.slug ?? '',
            planId: transaction?.input?.bookingEnginePlan?.id,
          })
        }
        navigate(onCreateNextPath(REGISTRATION_PATH.completion))
      }
    } catch (e) {
      // timeout error
      if (isAxiosError(e) && e.code === 'ECONNABORTED') {
        navigate(
          onCreateNextPath(REGISTRATION_PATH.loading, {
            queryParams: { transaction: transaction?.id },
          })
        )
      }
    }
    setReservationLoading(false)
  })

  const cancelPolicy = transaction?.input?.cancelPolicy ?? undefined
  // Card-free plans show a note clarifying how payment is settled. On-site
  // (現地決済) and invoice-billed (請求書払い) plans both skip the card, but the
  // guest-facing wording differs, so branch on the payment type — never show
  // the on-site note for an invoice plan (SB-2127). The cancelPolicy itself is
  // still shown verbatim from the backend.
  const isInvoiceBilledPayment = isInvoicePayment(priceData?.paymentType)
  const isOnSitePayment =
    !requiresPayment(priceData?.paymentType) && !isInvoiceBilledPayment
  const {
    isHotel = false,
    isSpa = false,
    isActivity = false,
  } = transaction?.input?.bookingEngineHotel ?? {
    isHotel: false,
    isSpa: false,
    isActivity: false,
  }
  const isReserved = Number.isFinite(transaction?.reservationId)

  const [existsRecent, setExistsRecent] = useState(false)
  const { data, isLoading: isRecentLoading } = useReservationsRecentApi({
    token,
    isHotel,
    isSpa,
    isActivity,
  })
  useEffect(() => {
    if ((data?.count ?? 0) > 0) {
      setExistsRecent(true)
    }
  }, [data])
  const reservationPath = useMemo(() => {
    if (isSpa) {
      return PATH.reservations.spas
    }
    if (isActivity) {
      return PATH.reservations.activities
    }
    return PATH.reservations.hotels
  }, [isSpa, isActivity])

  if (isReservationLoading || isTransactionLoading || isRecentLoading) {
    return (
      <Loader>
        <Text align="center">
          <Trans
            i18nKey={
              isReservationLoading
                ? `${translateNamespace}.reserving`
                : `${translateNamespace}.loading`
            }
          />
        </Text>
      </Loader>
    )
  }

  return (
    <ReservationLayout pageTitle={t(`${translateNamespace}.pageTitle`)}>
      <RegistrationStepper
        current={2}
        stepName={t(`${translateNamespace}.stepper`)}
      />
      <FormProvider {...formMethods}>
        <MultiColumn isSpColumnReverse>
          <MainColumn>
            {isPageError && (
              <ErrorCard>
                <Text>
                  <Trans i18nKey="frames.RegistrationNewFrame.error.confirmationError">
                    Sorry, An error has occurred We apologize for the
                    inconvenience.
                    <br />
                    Please change your search conditions and try again.
                  </Trans>
                </Text>
              </ErrorCard>
            )}
            {isReserved && transaction?.reservationId && (
              <ErrorCard>
                <Text>
                  <Trans
                    i18nKey={`${translateNamespace}.reserved`}
                    t={t}
                    components={{
                      reservationLink: (
                        <Link
                          className={styles.link}
                          to={onCreateNextPath(reservationPath.reservation, {
                            pathParams: {
                              reservation: transaction.reservationId.toString(),
                            },
                            clearQueryParams: true,
                          })}
                        />
                      ),
                    }}
                  />
                </Text>
              </ErrorCard>
            )}
            {!isReserved && existsRecent && (
              <ErrorCard warning>
                <Text>
                  <Trans
                    i18nKey={`${translateNamespace}.exists`}
                    t={t}
                    components={{
                      reservationsLink: (
                        <Link
                          className={styles.link}
                          to={reservationPath.root}
                        />
                      ),
                    }}
                  />
                </Text>
              </ErrorCard>
            )}
            <FormNote>
              <Text>{t(`${translateNamespace}.note`)}</Text>
            </FormNote>
            <form onSubmit={onSubmit}>
              {isOnSitePayment && (
                <FormNote>
                  <Text>{t(`${translateNamespace}.onSitePaymentNote`)}</Text>
                </FormNote>
              )}
              {isInvoiceBilledPayment && (
                <FormNote>
                  <Text>{t(`${translateNamespace}.invoicePaymentNote`)}</Text>
                  {transaction?.input?.invoiceAddressee && (
                    <Text>
                      {t(`${translateNamespace}.invoiceAddressee`, {
                        invoiceAddressee: transaction.input.invoiceAddressee,
                      })}
                    </Text>
                  )}
                </FormNote>
              )}
              {(transaction?.input?.optionAnswers !== undefined ||
                optionsData) && (
                <OptionAnswersSection
                  input={transaction?.input}
                  options={optionsData}
                  items={optionItems}
                />
              )}
              <CancelPolicySection cancelPolicy={cancelPolicy} />
              {publication?.notice && (
                <NotesSection note={publication.notice} />
              )}
              <div className={styles.agreementFormWrapper}>
                <AgreementForm />
              </div>
              {/* The generic plan-not-for-sale text would not tell the guest
                  that their invoice URL is what stopped working (SB-3056). */}
              {isInvoiceAccessKeyNoLongerValid && (
                <ErrorCard>
                  <Text>
                    {t(`${translateNamespace}.invoiceAccessKeyNoLongerValid`)}
                  </Text>
                </ErrorCard>
              )}
              {errorCord && !isInvoiceAccessKeyNoLongerValid && (
                <ErrorCard
                  statusCode={errorCord}
                  errorMessage={t(`${translateNamespace}.error`)}
                />
              )}
              <FormNote>
                <Text>{t(`${translateNamespace}.securityNote`)}</Text>
              </FormNote>
              <MultiColumn isSpColumnReverse>
                <Button
                  htmlType="button"
                  outline
                  variant="tertiary"
                  size="xmedium"
                  onClick={() => {
                    navigate(onCreateNextPath(REGISTRATION_PATH.registrations))
                  }}
                >
                  {t(`${translateNamespace}.button.gotoRegistration`)}
                </Button>
                <Button
                  htmlType="submit"
                  disabled={!isAgreed || !isPriceLoaded || isReserved}
                  variant="cta"
                  size="xmedium"
                >
                  {t(`${translateNamespace}.button.register`)}
                </Button>
              </MultiColumn>
            </form>
          </MainColumn>
          <SideColumn>
            <ReservationPanel
              isHotel={isHotel}
              reservationModal={
                <ReservationDetailButton transaction={transaction} />
              }
              checkInDate={transaction?.input?.checkInDate}
              checkOutDate={transaction?.input?.checkOutDate}
              numberOfAdults={transaction?.input?.numberOfAdults}
              numberOfChildren={transaction?.input?.numberOfChildren}
              numberOfInfants={transaction?.input?.numberOfInfants}
              roomCount={transaction?.input?.roomCount}
              property={transaction?.input?.bookingEngineHotel?.slug}
              room={transaction?.input?.bookingEngineRoom?.slug}
              plan={transaction?.input?.bookingEnginePlan?.slug}
              onApiError={(_error) => {
                setPageError(true)
                window.scrollTo(0, 0)
              }}
              onPriceDidLoad={() => {
                setPriceLoaded(true)
              }}
              transactionId={transactionId}
              reservationId={transaction?.reservationId}
            />
          </SideColumn>
        </MultiColumn>
      </FormProvider>
      <RegistrationConfirmationHead transactionId={transactionId} />
    </ReservationLayout>
  )
}
